import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { Result, Spin } from 'antd'

import {
  initializationState,
  langState,
  marketState,
  customerHmac,
  customerGUID,
  customerEmail,
  reachedUrlState,
} from 'src/data/common/commonAtoms'
import { PersistService } from 'src/data/common/PersistService'
import { authenticatedCustomerState } from 'src/data/customers/CustomersAtoms'
import { CustomersService } from 'src/data/customers/CustomersService'
import { accountDetails } from 'src/routes/NamedRoutes'
import { useValidationLoginHashShopify } from 'src/utils/customHooks'
import { formatEmail, isProduction } from 'src/utils/helper'

const SessionManager = (): JSX.Element => {
  const getValidation = useValidationLoginHashShopify()
  const [isSettingsDone, setStatus] = useRecoilState(initializationState)
  const setCustomer = useSetRecoilState(authenticatedCustomerState)
  const currentCustomer = useRecoilValue(authenticatedCustomerState)
  const setLang = useSetRecoilState(langState)
  const setMarket = useSetRecoilState(marketState)
  const setCustomerEmail = useSetRecoilState(customerEmail)
  const setCustomerHmac = useSetRecoilState(customerHmac)
  const setCustomerGUID = useSetRecoilState(customerGUID)
  const [urlReached, resetUrl] = useRecoilState(reachedUrlState)
  const [search] = useSearchParams()
  const [showError, setShowError] = useState(false)
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    const email = search.get('customerId')
    let customerSwitched = email?.replace('+', ' ') !== currentCustomer?.email?.replace('+', ' ')
    const eredtrgt = search.get('eredtrgt') // Order balance redirect link
    const childOrder = search.get('child_order') // Order balance child order param
    const sessionId = search.get('sessionId') // Google Analytics params
    const clientId = search.get('clientId') // Google Analytics params
    const customerHmac = search.get('customer_hmac') // For PrivateApp Authentication
    const customerGuid = search.get('id') // For PrivateApp Authentication

    if (customerHmac && customerGuid) {
      setCustomerEmail(email)
      setCustomerHmac(customerHmac)
      setCustomerGUID(customerGuid)
    }

    const getUser = async () => {
      const market = search.get('market') ?? 'CA'
      setMarket(market)
      try {
        if (!email) {
          if (!isProduction()) throw new Error('Email required')
          return
        }
        if (!customerGuid) throw new Error('Email required')

        const customer = await CustomersService.getCustomer(formatEmail(email), customerHmac, customerGuid)
        if (customer) {
          setCustomer(customer)
          let customerLang = customer.language
          if (!customerLang) {
            customerLang = /^fr\b/.test(navigator.language) ? 'fr' : 'en'
          } else if (customerLang.indexOf('_') > -1) {
            const lang = customerLang.split('_')[0]
            customerLang = ['en', 'fr'].includes(lang) ? lang : 'en'
          }
          setLang(customerLang)
          customerSwitched = false
          return customer
        } else {
          throw new Error('No customer is linked to this email')
        }
      } catch {
        setShowError(true)
        setTimeout(() => navigate(-1), 7000)
        throw new Error('No customer is linked to this email')
      }
    }
    const validationProcess = async () => {
      const isLoginValid = await getValidation()
      if (isLoginValid) {
        initializeSession()
      } else {
        throw new Error('Invalid login validation')
      }
    }
    const initializeSession = async () => {
      let customer = PersistService.fetch('authenticated_customer', 'local')
      // Need to fetch new CX if it's not the same as in local storage
      if (customerSwitched || !customer) {
        customer = await getUser()
      }
      setStatus(!!customer)
    }

    if (customerSwitched || !isSettingsDone) {
      validationProcess()
    } else {
      let analyticsParams = '' 
      if ((sessionId?.length ?? 0) > 0 && (clientId?.length ?? 0) > 0) {
        analyticsParams = `?sessionId=${sessionId}&clientId=${clientId}`
      }
      if (eredtrgt && email) {
        let redirectionLink = `${eredtrgt}${analyticsParams}`
        if (childOrder === 'true') {
          redirectionLink += '&child_order=true'
        }
        navigate(redirectionLink, { replace: true })  
      } else {
        let urlToGo = urlReached ?? accountDetails
        urlToGo += analyticsParams
        resetUrl(null)
        navigate(urlToGo, { replace: true })  
      }
    }
  }, [isSettingsDone, setStatus, setCustomer, search, navigate, setLang, urlReached, resetUrl, setMarket, getValidation, currentCustomer?.email, setCustomerEmail, setCustomerHmac, setCustomerGUID])
  return (
    (showError ?
      <Result
        status="error"
        title={t('common:errors.title')}
        subTitle={`${t('common:errors.no_customer_found')} ${search.get('customerId')}. ${t('common:errors.redirection')}`}
      />
      : <Spin
        spinning={true}
        wrapperClassName="default-layout__wrapper-spinner"
        className="default-layout__spinner"
        tip={t('common:loading')}
      />)
  )
}

export default SessionManager
